import type { DefaultLegendContentProps, TooltipPayloadEntry, TooltipValueType } from 'recharts';
import { ChartLegendContent, type ChartConfig } from '@/components/ui/chart';
import { formatPLN, formatPLNCompact } from '@/utils/currency';

export { formatPLN, formatPLNCompact };

// Matches the unexported `TooltipNameType` used internally by ChartTooltipContent.
type TooltipNameType = number | string;

export const MONTH_LABELS = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
] as const;

export function monthKey(year: number, index: number): string {
  return `${year}-${String(index + 1).padStart(2, '0')}`;
}

export function formatAxis(v: number): string {
  return v.toLocaleString('pl-PL', { maximumFractionDigits: 0 });
}

export function formatPct(v: number): string {
  return v.toLocaleString('pl-PL', { minimumFractionDigits: 1, maximumFractionDigits: 1 });
}

/**
 * Shared formatter for ChartTooltipContent's `formatter` prop: renders a color
 * swatch, the series label from `config`, and the value formatted as PLN.
 *
 * `options.hideZero` skips rendering a row's content when its value is 0 — used
 * by the stacked charts (tags, payment methods) so months with no activity in a
 * given series don't clutter the tooltip. Note: ChartTooltipContent always
 * renders a wrapping row `<div>` per payload entry regardless of what the
 * formatter returns, so a "hidden" zero row still occupies a (contentless,
 * near-zero-height) grid row — there is no way to fully remove the row from
 * inside the formatter itself without restructuring how each chart wires up
 * ChartTooltip's `content`. Returning `null` renders nothing visible in that
 * row, which is the best available option given the formatter-only contract.
 */
export function plnTooltipFormatter(config: ChartConfig, options?: { hideZero?: boolean }) {
  return (
    value: TooltipValueType | undefined,
    name: TooltipNameType | undefined,
    item: TooltipPayloadEntry<TooltipValueType, TooltipNameType>
  ) => {
    if (options?.hideZero && Number(value) === 0) return null;
    return (
      <>
        <div className="size-2.5 shrink-0 rounded-[2px]" style={{ background: item.color }} />
        {config[name as keyof typeof config]?.label ?? String(name)}
        <span className="text-foreground ml-auto font-mono font-medium tabular-nums">
          {formatPLN(Number(value))} PLN
        </span>
      </>
    );
  };
}

/**
 * Legend content renderer that pins legend items to the given dataKey order.
 * Recharts 3 sorts legend payload entries alphabetically by dataKey, which puts
 * e.g. "cost" before "revenue" — the legend must mirror the visual bar order.
 */
export function orderedLegendContent(order: string[]) {
  return function OrderedLegend(props: DefaultLegendContentProps) {
    const payload = [...(props.payload ?? [])].sort(
      (a, b) => order.indexOf(String(a.dataKey)) - order.indexOf(String(b.dataKey)),
    );
    return <ChartLegendContent verticalAlign={props.verticalAlign} payload={payload} />;
  };
}

/**
 * Custom tooltip content for grouped two-series PLN bar charts that also need a
 * computed summary row (revenue vs cost → margin; cash in vs out → net). Styled
 * to match ChartTooltipContent, but receives `config` as a prop instead of via
 * chart context so the extra delta row can be rendered below a divider.
 */
export function PlnDeltaTooltip({
  active,
  payload,
  label,
  year,
  config,
  deltaLabel,
  deltaKey,
}: {
  active?: boolean;
  payload?: TooltipPayloadEntry<TooltipValueType, TooltipNameType>[];
  label?: TooltipNameType;
  year: number;
  config: ChartConfig;
  deltaLabel: string;
  deltaKey: string;
}) {
  if (!active || !payload?.length) return null;
  const row = payload[0]?.payload as Record<string, number> | undefined;
  const delta = row?.[deltaKey] ?? 0;
  return (
    <div className="border-border/50 bg-background grid min-w-[10rem] items-start gap-1.5 rounded-lg border px-2.5 py-1.5 text-xs shadow-xl">
      <div className="font-medium">{`${String(label)} ${year}`}</div>
      <div className="grid gap-1.5">
        {payload.map((item) => (
          <div key={String(item.dataKey)} className="flex w-full items-center gap-2">
            <div className="size-2.5 shrink-0 rounded-[2px]" style={{ background: item.color }} />
            <span className="text-muted-foreground">
              {config[String(item.dataKey)]?.label ?? String(item.name)}
            </span>
            <span className="text-foreground ml-auto pl-4 font-mono font-medium tabular-nums">
              {formatPLN(Number(item.value))} PLN
            </span>
          </div>
        ))}
        <div className="border-border/50 flex w-full items-center gap-2 border-t pt-1.5">
          <div className="size-2.5 shrink-0" aria-hidden="true" />
          <span className="text-muted-foreground">{deltaLabel}</span>
          <span className="text-foreground ml-auto pl-4 font-mono font-medium tabular-nums">
            {formatPLN(delta)} PLN
          </span>
        </div>
      </div>
    </div>
  );
}

/**
 * Twin of `plnTooltipFormatter` for percentage-valued series: same color swatch
 * and label rendering, but the value is formatted as a fixed-point percentage
 * instead of PLN currency.
 */
export function pctTooltipFormatter(config: ChartConfig, options?: { hideZero?: boolean }) {
  return (
    value: TooltipValueType | undefined,
    name: TooltipNameType | undefined,
    item: TooltipPayloadEntry<TooltipValueType, TooltipNameType>
  ) => {
    if (options?.hideZero && Number(value) === 0) return null;
    return (
      <>
        <div className="size-2.5 shrink-0 rounded-[2px]" style={{ background: item.color }} />
        {config[name as keyof typeof config]?.label ?? String(name)}
        <span className="text-foreground ml-auto font-mono font-medium tabular-nums">
          {formatPct(Number(value))}%
        </span>
      </>
    );
  };
}
